import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Divider,
  InputAdornment,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPacks,
  fetchTiers,
  createRate,
  updateRate,
  deleteRate,
  fetchPlans,
  createTier,
  updateTier,
  deleteTier,
  updatePack,
  createPack,
} from '../../store/pricingSlice';
import { fetchPrestations } from '../../store/prestationSlice';
import { pricingService, vehicleSettingsService, prestationService, vehicleCategoryService } from '../../services/api';
import type { PricingPack, PricingTier, SeasonalRate, VehicleCategory, VehicleType, Prestation, PricingPlan, PrestationTierRate, CategoryPrestationRate, TypePrestationRate } from '../../types';
import { format, parseISO, getDaysInMonth, getDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';

const MONTH_NAMES = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
const DAY_ABBREVS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

const PricingGrid: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { packs, tiers, plans } = useAppSelector((state) => state.pricing);
  const { prestations } = useAppSelector((state) => state.prestation);
  
  // View mode: 'vehicles' or 'prestations'
  const [viewMode, setViewMode] = useState<'vehicles' | 'prestations'>('vehicles');
  
  // State management
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [categories, setCategories] = useState<VehicleCategory[]>([]);
  const [selectedType, setSelectedType] = useState<number | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [selectedPack, setSelectedPack] = useState<PricingPack | null>(null);
  /** Seasonal "tarif de base" rows for the selected pack only — local state avoids Redux fetch races when switching plan/category/pack */
  const [seasonalRates, setSeasonalRates] = useState<SeasonalRate[]>([]);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<SeasonalRate | null>(null);
  const [prestationDialogOpen, setPrestationDialogOpen] = useState(false);
  const [rateFormData, setRateFormData] = useState({
    start_date: '',
    end_date: '',
    prices: {} as Record<number, number>,
    color: '#E91E63',
  });

  // Prestation pricing state
  // Default rates per prestation/plan/tier (associé aux tarifs - first table)
  const [prestationTierRates, setPrestationTierRates] = useState<PrestationTierRate[]>([]);
  // Category-level custom rates (override default)
  const [prestationRates, setPrestationRates] = useState<CategoryPrestationRate[]>([]);
  // Type-level custom rates (override default when category has no custom rate)
  const [typeRates, setTypeRates] = useState<TypePrestationRate[]>([]);
  const [prestationRateDialogOpen, setPrestationRateDialogOpen] = useState(false);
  const [selectedPrestationForRate, setSelectedPrestationForRate] = useState<Prestation | null>(null);
  const [selectedCategoryForRate, setSelectedCategoryForRate] = useState<number | null>(null);
  const [prestationRateFormData, setPrestationRateFormData] = useState({
    prestation_id: '',
    prices: {} as Record<number, number>,
  });
  
  // Default rate dialog (for first table - associé aux tarifs)
  const [defaultRateDialogOpen, setDefaultRateDialogOpen] = useState(false);
  const [editingDefaultRate, setEditingDefaultRate] = useState<Prestation | null>(null);
  const [defaultRateFormData, setDefaultRateFormData] = useState({
    prestation_id: '',
    prices: {} as Record<number, number>,
  });

  // Custom tarif dialog state (null = opened from "associé aux tarifs" table, user must choose category or type)
  const [customTarifDialogOpen, setCustomTarifDialogOpen] = useState(false);
  const [customTarifType, setCustomTarifType] = useState<'category' | 'type' | null>('category');
  /** When set, we're editing existing type rates (prestationId + typeId); on save we update instead of create. */
  const [editingTypeRate, setEditingTypeRate] = useState<{ prestationId: number; typeId: number } | null>(null);
  const [customTarifFormData, setCustomTarifFormData] = useState({
    prestation_id: '',
    category_id: '',
    type_id: '',
    prices: {} as Record<number, number>,
  });

  // Modèle de tarification dialog
  const [tierModelDialogOpen, setTierModelDialogOpen] = useState(false);
  const [tierFormDialogOpen, setTierFormDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null);
  const [tierFormData, setTierFormData] = useState({
    name: '',
    min_days: 1,
    max_days: 5,
    sort_order: 0,
  });

  // Pack config state
  const [packConfig, setPackConfig] = useState({
    franchise_amount: 0,
    default_rate: 0,
  });

  // Create pack dialog (from grid - stay on same page)
  const [createPackDialogOpen, setCreatePackDialogOpen] = useState(false);
  const [createPackFormData, setCreatePackFormData] = useState({
    pricing_plan_id: '',
    vehicle_category_id: '',
    name: '',
    description: '',
    franchise_amount: 0,
    default_rate: 0,
    color: '#1976D2',
    is_active: true,
  });

  // Standard rate dialog state
  const [standardRateDialogOpen, setStandardRateDialogOpen] = useState(false);
  const [standardRateError, setStandardRateError] = useState<string | null>(null);
  const [standardRateFormData, setStandardRateFormData] = useState({
    rate_ht: 0,
    tax_rate: 20,
  });

  // Update pack config when selectedPack changes
  useEffect(() => {
    if (selectedPack) {
      setPackConfig({
        franchise_amount: selectedPack.franchise_amount || 0,
        default_rate: selectedPack.default_rate || 0,
      });
    }
  }, [selectedPack]);

  // Load initial data
  useEffect(() => {
    dispatch(fetchPacks());
    dispatch(fetchTiers());
    dispatch(fetchPlans());
    dispatch(fetchPrestations());
    loadTypesAndCategories();
  }, [dispatch]);

  // Auto-select plan from URL
  useEffect(() => {
    const planId = searchParams.get('plan');
    if (planId && plans.length > 0) {
      const plan = plans.find(p => p.id === parseInt(planId));
      if (plan) setSelectedPlan(plan);
    } else if (plans.length > 0 && !selectedPlan) {
      setSelectedPlan(plans[0]);
    }
  }, [plans, searchParams, selectedPlan]);

  // Load prestation rates (default, category, type) when plan changes
  useEffect(() => {
    if (selectedPlan && viewMode === 'prestations') {
      loadPrestationTierRates();
      loadPrestationRates();
      loadTypeRates();
    }
  }, [selectedPlan, viewMode]);

  const loadTypesAndCategories = async () => {
    try {
      const response = await vehicleSettingsService.getSettings();
      setTypes(response.data.data?.types || []);
      setCategories(response.data.data?.categories || []);
    } catch (error) {
      console.error('Error loading types and categories:', error);
    }
  };

  const loadPrestationTierRates = async () => {
    if (!selectedPlan) return;
    try {
      const response = await prestationService.getPrestationTierRates({ pricing_plan_id: selectedPlan.id });
      setPrestationTierRates(response.data.data || []);
    } catch (error) {
      console.error('Error loading prestation tier rates:', error);
      setPrestationTierRates([]);
    }
  };

  const loadPrestationRates = async () => {
    try {
      const allRates: CategoryPrestationRate[] = [];
      for (const prestation of prestations) {
        try {
          const response = await prestationService.getPrestationRates(prestation.id);
          if (response.data.data) {
            allRates.push(...response.data.data);
          }
        } catch (e) {
          // Ignore errors for individual prestations
        }
      }
      setPrestationRates(allRates);
    } catch (error) {
      console.error('Error loading prestation rates:', error);
    }
  };

  const loadTypeRates = async () => {
    try {
      const response = await prestationService.getTypeRates();
      setTypeRates(response.data.data || []);
    } catch (error) {
      console.error('Error loading type prestation rates:', error);
      setTypeRates([]);
    }
  };

  // Filter categories by selected type
  const filteredCategories = useMemo(() => {
    if (!selectedType) return categories;
    return categories.filter(cat => cat.vehicle_type_id === selectedType);
  }, [categories, selectedType]);

  // Packs for this plan + this category only (excludes "universal" packs — those share one seasonal_rates set for all categories)
  const filteredPacks = useMemo(() => {
    if (!selectedPlan || !selectedCategory) return [];
    return packs
      .filter((pack) => pack.pricing_plan_id === selectedPlan.id)
      .filter((pack) => Number(pack.vehicle_category_id) === Number(selectedCategory));
  }, [packs, selectedPlan, selectedCategory]);

  // Get selected category rate
  const selectedCategoryRate = useMemo(() => {
    if (!selectedCategory) return null;
    return categories.find(cat => cat.id === selectedCategory);
  }, [categories, selectedCategory]);

  // Handle pack selection (sync id first so seasonal rates fetch the right pack; then enrich from API)
  const handlePackSelect = useCallback(async (pack: PricingPack) => {
    setSelectedPack(pack);
    try {
      const response = await pricingService.getPack(pack.id);
      setSelectedPack(response.data.data);
    } catch (error) {
      console.error('Error loading pack:', error);
      setSelectedPack(pack);
    }
  }, []);

  // Keep edited pack aligned with the selected category: each category has its own packs / seasonal rates
  useEffect(() => {
    if (!selectedPlan) return;
    if (!selectedCategory) {
      setSelectedPack(null);
      return;
    }
    if (filteredPacks.length === 0) {
      setSelectedPack(null);
      return;
    }
    const stillValid =
      selectedPack != null && filteredPacks.some((p) => p.id === selectedPack.id);
    if (stillValid) return;
    const next = filteredPacks[0];
    setSelectedPack(next);
    void (async () => {
      try {
        const response = await pricingService.getPack(next.id);
        setSelectedPack(response.data.data);
      } catch (error) {
        console.error('Error loading pack:', error);
      }
    })();
  }, [selectedPlan, selectedCategory, filteredPacks, selectedPack]);

  const refetchSeasonalRates = useCallback(async (packId: number) => {
    try {
      const res = await pricingService.getRates({ pack_id: packId });
      const rows = res.data?.data ?? [];
      setSeasonalRates(Array.isArray(rows) ? rows : []);
    } catch {
      setSeasonalRates([]);
    }
  }, []);

  useEffect(() => {
    const packId = selectedPack?.id;
    if (!packId) {
      setSeasonalRates([]);
      return;
    }
    let cancelled = false;
    setSeasonalRates([]);
    void (async () => {
      try {
        const res = await pricingService.getRates({ pack_id: packId });
        if (cancelled) return;
        const rows = res.data?.data ?? [];
        setSeasonalRates(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setSeasonalRates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPack?.id]);

  const openCreatePackDialog = () => {
    if (!selectedPlan) return;
    setCreatePackFormData({
      pricing_plan_id: selectedPlan.id.toString(),
      vehicle_category_id: selectedCategory ? selectedCategory.toString() : '',
      name: '',
      description: '',
      franchise_amount: 0,
      default_rate: 0,
      color: '#1976D2',
      is_active: true,
    });
    setCreatePackDialogOpen(true);
  };

  const handleCreatePackSubmit = async () => {
    if (!createPackFormData.name) return;
    try {
      const data = {
        ...createPackFormData,
        pricing_plan_id: createPackFormData.pricing_plan_id ? parseInt(createPackFormData.pricing_plan_id) : undefined,
        vehicle_category_id: createPackFormData.vehicle_category_id ? parseInt(createPackFormData.vehicle_category_id) : undefined,
      };
      const newPack = await dispatch(createPack(data)).unwrap();
      dispatch(fetchPacks());
      setCreatePackDialogOpen(false);
      setSelectedPack(newPack);
    } catch (err) {
      console.error('Error creating pack:', err);
    }
  };

  // State for editing rate groups
  const [editingRateGroup, setEditingRateGroup] = useState<{ 
    startDate: string; 
    endDate: string; 
    color: string; 
    prices: Record<number, number>; 
    ids: Record<number, number>;
  } | null>(null);

  // Handle rate dialog
  const handleOpenRateDialog = (rate?: SeasonalRate) => {
    if (rate) {
      setEditingRate(rate);
      setEditingRateGroup(null);
      setRateFormData({
        start_date: rate.start_date.split('T')[0],
        end_date: rate.end_date.split('T')[0],
        prices: { [rate.pricing_tier_id]: rate.price_ht },
        color: rate.color || '#E91E63',
      });
    } else {
      setEditingRate(null);
      setEditingRateGroup(null);
      const defaultPrices: Record<number, number> = {};
      sortedTiers.forEach(tier => {
        defaultPrices[tier.id] = 0;
      });
      setRateFormData({
        start_date: '',
        end_date: '',
        prices: defaultPrices,
        color: '#E91E63',
      });
    }
    setRateDialogOpen(true);
  };

  // Handle rate group edit - loads all prices for the date range
  const handleEditRateGroup = (group: { startDate: string; endDate: string; color: string; prices: Record<number, number>; ids: Record<number, number> }) => {
    setEditingRate(null);
    setEditingRateGroup(group);
    setRateFormData({
      start_date: group.startDate.split('T')[0],
      end_date: group.endDate.split('T')[0],
      prices: { ...group.prices },
      color: group.color || '#E91E63',
    });
    setRateDialogOpen(true);
  };

  const handleCloseRateDialog = () => {
    setRateDialogOpen(false);
    setEditingRate(null);
    setEditingRateGroup(null);
  };

  const handleSaveRate = async () => {
    if (!selectedPack) return;
    try {
      if (editingRateGroup) {
        // Update existing rates in the group
        const updatePromises = Object.entries(rateFormData.prices).map(async ([tierId, price]) => {
          const tierIdNum = parseInt(tierId);
          const existingRateId = editingRateGroup.ids[tierIdNum];
          
          if (existingRateId && price > 0) {
            // Update existing rate
            const data = {
              pricing_pack_id: selectedPack.id,
              pricing_tier_id: tierIdNum,
              start_date: rateFormData.start_date,
              end_date: rateFormData.end_date,
              price_ht: price,
              color: rateFormData.color,
            };
            return dispatch(updateRate({ id: existingRateId, data })).unwrap();
          } else if (!existingRateId && price > 0) {
            // Create new rate for this tier
            const data = {
              pricing_pack_id: selectedPack.id,
              pricing_tier_id: tierIdNum,
              start_date: rateFormData.start_date,
              end_date: rateFormData.end_date,
              price_ht: price,
              color: rateFormData.color,
            };
            return dispatch(createRate(data)).unwrap();
          } else if (existingRateId && price === 0) {
            // Delete rate if price is 0
            return dispatch(deleteRate(existingRateId)).unwrap();
          }
        });
        await Promise.all(updatePromises);
      } else if (editingRate) {
        const data = {
          pricing_pack_id: selectedPack.id,
          pricing_tier_id: editingRate.pricing_tier_id,
          start_date: rateFormData.start_date,
          end_date: rateFormData.end_date,
          price_ht: rateFormData.prices[editingRate.pricing_tier_id] || 0,
          color: rateFormData.color,
        };
        await dispatch(updateRate({ id: editingRate.id, data })).unwrap();
      } else {
        // Create new rates
        const promises = Object.entries(rateFormData.prices)
          .filter(([_, price]) => price > 0)
          .map(([tierId, price]) => {
            const data = {
              pricing_pack_id: selectedPack.id,
              pricing_tier_id: parseInt(tierId),
              start_date: rateFormData.start_date,
              end_date: rateFormData.end_date,
              price_ht: price,
              color: rateFormData.color,
            };
            return dispatch(createRate(data)).unwrap();
          });
        await Promise.all(promises);
      }
      handleCloseRateDialog();
      await refetchSeasonalRates(selectedPack.id);
    } catch (err) {
      console.error('Error saving rate:', err);
    }
  };

  const handleDeleteRate = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) {
      try {
        await dispatch(deleteRate(id)).unwrap();
        if (selectedPack) await refetchSeasonalRates(selectedPack.id);
      } catch (err) {
        console.error('Error deleting rate:', err);
      }
    }
  };

  // Tier CRUD handlers
  const handleOpenTierForm = (tier?: PricingTier) => {
    if (tier) {
      setEditingTier(tier);
      setTierFormData({
        name: tier.name,
        min_days: tier.min_days,
        max_days: tier.max_days,
        sort_order: tier.sort_order,
      });
    } else {
      setEditingTier(null);
      const maxSortOrder = tiers.length > 0 ? Math.max(...tiers.map(t => t.sort_order)) : 0;
      setTierFormData({
        name: '',
        min_days: 1,
        max_days: 5,
        sort_order: maxSortOrder + 1,
      });
    }
    setTierFormDialogOpen(true);
  };

  const handleCloseTierForm = () => {
    setTierFormDialogOpen(false);
    setEditingTier(null);
  };

  const handleSaveTier = async () => {
    try {
      if (editingTier) {
        await dispatch(updateTier({ id: editingTier.id, data: tierFormData })).unwrap();
      } else {
        await dispatch(createTier(tierFormData)).unwrap();
      }
      handleCloseTierForm();
      dispatch(fetchTiers());
    } catch (err) {
      console.error('Error saving tier:', err);
    }
  };

  const handleDeleteTier = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce niveau tarifaire ?')) {
      try {
        await dispatch(deleteTier(id)).unwrap();
        dispatch(fetchTiers());
      } catch (err) {
        console.error('Error deleting tier:', err);
      }
    }
  };

  // Handle prestations
  const handleSyncPrestations = async (prestationIds: number[]) => {
    if (!selectedPack) return;
    try {
      await pricingService.syncPackPrestations(selectedPack.id, prestationIds);
      const response = await pricingService.getPack(selectedPack.id);
      setSelectedPack(response.data.data);
    } catch (err) {
      console.error('Error syncing prestations:', err);
    }
  };

  const handleAddPrestation = (prestation: Prestation) => {
    if (!selectedPack) return;
    const currentIds = selectedPack.included_prestations?.map(p => p.id) || [];
    if (!currentIds.includes(prestation.id)) {
      handleSyncPrestations([...currentIds, prestation.id]);
    }
  };

  const handleRemovePrestation = (prestationId: number) => {
    if (!selectedPack) return;
    const currentIds = selectedPack.included_prestations?.map(p => p.id) || [];
    handleSyncPrestations(currentIds.filter(id => id !== prestationId));
  };

  // Save pack config
  const handleSavePackConfig = async () => {
    if (!selectedPack) return;
    try {
      await dispatch(updatePack({ 
        id: selectedPack.id, 
        data: {
          franchise_amount: packConfig.franchise_amount,
          default_rate: packConfig.default_rate,
        }
      })).unwrap();
      // Refresh pack data
      const response = await pricingService.getPack(selectedPack.id);
      setSelectedPack(response.data.data);
    } catch (err) {
      console.error('Error updating pack config:', err);
    }
  };

  // Open standard rate dialog
  const handleOpenStandardRateDialog = () => {
    if (!selectedCategoryRate) return;
    setStandardRateFormData({
      rate_ht: selectedCategoryRate.rate_ht || 0,
      tax_rate: selectedCategoryRate.tax_rate || 20,
    });
    setStandardRateDialogOpen(true);
  };

  // Save standard rate
  const handleSaveStandardRate = async () => {
    if (!selectedCategory) return;
    setStandardRateError(null);
    try {
      const rate_ttc = standardRateFormData.rate_ht * (1 + standardRateFormData.tax_rate / 100);
      await vehicleSettingsService.updateCategory(selectedCategory as number, {
        rate_ht: standardRateFormData.rate_ht,
        rate_ttc: rate_ttc,
        tax_rate: standardRateFormData.tax_rate,
      });
      // Refresh categories
      await loadTypesAndCategories();
      setStandardRateDialogOpen(false);
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.errors?.rate_ht?.[0] || err.message || 'Erreur lors de l\'enregistrement';
      setStandardRateError(typeof message === 'string' ? message : JSON.stringify(message));
      console.error('Error updating standard rate:', err);
    }
  };

  // Prestation rate handlers
  const handleOpenPrestationRateDialog = (prestation?: Prestation, categoryId?: number) => {
    if (prestation) {
      setSelectedPrestationForRate(prestation);
      setSelectedCategoryForRate(categoryId || null);
      const prices: Record<number, number> = {};
      prestationRates
        .filter(r => r.prestation_id === prestation.id && (categoryId ? r.vehicle_category_id === categoryId : true))
        .forEach(r => {
          prices[r.pricing_tier_id] = r.rate_ht;
        });
      setPrestationRateFormData({
        prestation_id: prestation.id.toString(),
        prices,
      });
    } else {
      setSelectedPrestationForRate(null);
      setSelectedCategoryForRate(null);
      setPrestationRateFormData({
        prestation_id: '',
        prices: {},
      });
    }
    setPrestationRateDialogOpen(true);
  };

  const handleSavePrestationRate = async () => {
    if (!prestationRateFormData.prestation_id) return;
    try {
      const prestationId = parseInt(prestationRateFormData.prestation_id);
      // Use selectedCategoryForRate if set, otherwise fall back to selectedCategory
      const categoryToUse = selectedCategoryForRate || selectedCategory as number || 0;
      for (const [tierId, price] of Object.entries(prestationRateFormData.prices)) {
        if (price > 0) {
          await prestationService.setCategoryRate(prestationId, {
            vehicle_category_id: categoryToUse,
            pricing_tier_id: parseInt(tierId),
            rate_ht: price,
          });
        }
      }
      setPrestationRateDialogOpen(false);
      setSelectedCategoryForRate(null);
      loadPrestationRates();
    } catch (err) {
      console.error('Error saving prestation rate:', err);
    }
  };

  // Open default rate dialog (for first table - associé aux tarifs)
  const handleOpenDefaultRateDialog = (prestation: Prestation) => {
    if (!selectedPlan) return;
    setEditingDefaultRate(prestation);
    const prices: Record<number, number> = {};
    prestationTierRates
      .filter(r => r.prestation_id === prestation.id && r.pricing_plan_id === selectedPlan.id)
      .forEach(r => { prices[r.pricing_tier_id] = Number(r.rate_ht); });
    setDefaultRateFormData({
      prestation_id: prestation.id.toString(),
      prices,
    });
    setDefaultRateDialogOpen(true);
  };

  const handleSaveDefaultRate = async () => {
    if (!selectedPlan || !defaultRateFormData.prestation_id) return;
    try {
      const prestationId = parseInt(defaultRateFormData.prestation_id);
      for (const [tierIdStr, price] of Object.entries(defaultRateFormData.prices)) {
        const tierId = parseInt(tierIdStr);
        if (price > 0) {
          const existing = prestationTierRates.find(
            r => r.prestation_id === prestationId && r.pricing_plan_id === selectedPlan.id && r.pricing_tier_id === tierId
          );
          if (existing) {
            await prestationService.updatePrestationTierRate(existing.id, { rate_ht: price });
          } else {
            await prestationService.createPrestationTierRate({
              prestation_id: prestationId,
              pricing_plan_id: selectedPlan.id,
              pricing_tier_id: tierId,
              rate_ht: price,
            });
          }
        }
      }
      setDefaultRateDialogOpen(false);
      setEditingDefaultRate(null);
      setDefaultRateFormData({ prestation_id: '', prices: {} });
      loadPrestationTierRates();
    } catch (err) {
      console.error('Error saving default rate:', err);
    }
  };

  // Open custom tarif dialog to edit type-level rate
  const handleOpenTypeRateDialog = (prestation: Prestation, typeId: number) => {
    setCustomTarifType('type');
    setEditingTypeRate({ prestationId: prestation.id, typeId });
    const prices: Record<number, number> = {};
    typeRates
      .filter(r => r.prestation_id === prestation.id && r.vehicle_type_id === typeId)
      .forEach(r => { prices[r.pricing_tier_id] = Number(r.rate_ht); });
    setCustomTarifFormData({
      prestation_id: prestation.id.toString(),
      category_id: '',
      type_id: typeId.toString(),
      prices,
    });
    setCustomTarifDialogOpen(true);
  };

  // Save custom tarif
  const handleSaveCustomTarif = async () => {
    if (!customTarifFormData.prestation_id) return;
    try {
      const prestationId = parseInt(customTarifFormData.prestation_id);

      if (customTarifType === 'category' && customTarifFormData.category_id) {
        const categoryId = parseInt(customTarifFormData.category_id);
        for (const [tierId, price] of Object.entries(customTarifFormData.prices)) {
          if (price > 0) {
            await prestationService.setCategoryRate(prestationId, {
              vehicle_category_id: categoryId,
              pricing_tier_id: parseInt(tierId),
              rate_ht: price,
            });
          }
        }
        loadPrestationRates();
      } else if (customTarifType === 'type' && customTarifFormData.type_id) {
        const typeId = parseInt(customTarifFormData.type_id);
        if (editingTypeRate && editingTypeRate.prestationId === prestationId && editingTypeRate.typeId === typeId) {
          // Update existing type rates
          for (const [tierIdStr, price] of Object.entries(customTarifFormData.prices)) {
            const tierId = parseInt(tierIdStr);
            const existing = typeRates.find(
              r => r.prestation_id === prestationId && r.vehicle_type_id === typeId && r.pricing_tier_id === tierId
            );
            if (existing) {
              await prestationService.updateTypeRate(existing.id, { rate_ht: price });
            } else if (price > 0) {
              await prestationService.createTypeRate({
                prestation_id: prestationId,
                vehicle_type_id: typeId,
                pricing_tier_id: tierId,
                rate_ht: price,
              });
            }
          }
        } else {
          // Create new type rates (one per tier with price > 0)
          for (const [tierId, price] of Object.entries(customTarifFormData.prices)) {
            if (price > 0) {
              await prestationService.createTypeRate({
                prestation_id: prestationId,
                vehicle_type_id: typeId,
                pricing_tier_id: parseInt(tierId),
                rate_ht: price,
              });
            }
          }
        }
        loadTypeRates();
      }

      setCustomTarifDialogOpen(false);
      setEditingTypeRate(null);
      setCustomTarifFormData({ prestation_id: '', category_id: '', type_id: '', prices: {} });
      if (customTarifType === 'category') loadPrestationRates();
    } catch (err) {
      console.error('Error saving custom tarif:', err);
    }
  };

  const sortedTiers = [...tiers].sort((a, b) => a.sort_order - b.sort_order);

  const ratesForSelectedPack = useMemo(() => {
    if (!selectedPack) return [];
    return seasonalRates.filter((r) => r.pricing_pack_id === selectedPack.id);
  }, [seasonalRates, selectedPack]);

  // Group rates by date range
  const groupedRates = useMemo(() => {
    const groups: { [key: string]: { startDate: string; endDate: string; color: string; prices: { [tierId: number]: number }; ids: { [tierId: number]: number } } } = {};
    
    ratesForSelectedPack.forEach(rate => {
      const key = `${rate.start_date}-${rate.end_date}`;
      if (!groups[key]) {
        groups[key] = {
          startDate: rate.start_date,
          endDate: rate.end_date,
          color: rate.color || '#2196F3',
          prices: {},
          ids: {},
        };
      }
      groups[key].prices[rate.pricing_tier_id] = rate.price_ht;
      groups[key].ids[rate.pricing_tier_id] = rate.id;
      if (rate.color) groups[key].color = rate.color;
    });

    return Object.values(groups).sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
  }, [ratesForSelectedPack]);

  // Chart data
  const chartData = useMemo(() => {
    if (!selectedPack || ratesForSelectedPack.length === 0) return [];
    
    const dateMap: { [key: string]: any } = {};
    
    ratesForSelectedPack.forEach(rate => {
      const start = parseISO(rate.start_date);
      const key = format(start, 'dd MMM,yy', { locale: fr });
      if (!dateMap[key]) {
        dateMap[key] = { date: key };
      }
      const tier = tiers.find(t => t.id === rate.pricing_tier_id);
      if (tier) {
        dateMap[key][tier.name] = rate.price_ht;
      }
    });

    return Object.values(dateMap);
  }, [ratesForSelectedPack, tiers, selectedPack]);

  // Calendar data
  const currentYear = new Date().getFullYear();
  const calendarData = useMemo(() => {
    return MONTH_NAMES.map((monthName, monthIndex) => {
      const daysInMonth = getDaysInMonth(new Date(currentYear, monthIndex));
      const days = Array.from({ length: 31 }, (_, i) => {
        if (i >= daysInMonth) return null;
        const date = new Date(currentYear, monthIndex, i + 1);
        const dateStr = format(date, 'yyyy-MM-dd');
        
        const rate = ratesForSelectedPack.find(r => {
          const start = r.start_date.split('T')[0];
          const end = r.end_date.split('T')[0];
          return dateStr >= start && dateStr <= end;
        });
        
        return {
          day: i + 1,
          dayAbbrev: DAY_ABBREVS[getDay(date) === 0 ? 6 : getDay(date) - 1],
          color: rate?.color || null,
        };
      });
      return { monthName, days };
    });
  }, [ratesForSelectedPack, currentYear]);

  /**
   * Resolution rule: category overrides type, type overrides default.
   * For a given (prestation, tier, category):
   * 1. If category_prestation_rate exists → use that
   * 2. Else if type_prestation_rate exists (for category's vehicle_type_id) → use that
   * 3. Else → use prestation_tier_rate (default rate for this prestation/plan/tier)
   */
  const getResolvedRate = (prestationId: number, tierId: number, category: VehicleCategory): number | null => {
    // 1. Check category custom rate
    const categoryRate = prestationRates.find(
      r => r.prestation_id === prestationId && r.vehicle_category_id === category.id && r.pricing_tier_id === tierId
    );
    if (categoryRate) return Number(categoryRate.rate_ht);
    
    // 2. Check type custom rate
    const typeId = category.vehicle_type_id;
    if (typeId != null) {
      const typeRate = typeRates.find(
        r => r.prestation_id === prestationId && r.vehicle_type_id === typeId && r.pricing_tier_id === tierId
      );
      if (typeRate) return Number(typeRate.rate_ht);
    }
    
    // 3. Fallback to default rate (prestation_tier_rate for this plan)
    if (!selectedPlan) return null;
    const defaultRate = prestationTierRates.find(
      r => r.prestation_id === prestationId && r.pricing_plan_id === selectedPlan.id && r.pricing_tier_id === tierId
    );
    return defaultRate ? Number(defaultRate.rate_ht) : null;
  };

  /** Get default rate for (prestation, tier) from first table (associé aux tarifs). */
  const getDefaultRate = (prestationId: number, tierId: number): number | null => {
    if (!selectedPlan) return null;
    const rate = prestationTierRates.find(
      r => r.prestation_id === prestationId && r.pricing_plan_id === selectedPlan.id && r.pricing_tier_id === tierId
    );
    return rate ? Number(rate.rate_ht) : null;
  };

  /** Whether this prestation has a default rate configured. */
  const prestationHasDefaultRate = (prestationId: number) =>
    selectedPlan != null && prestationTierRates.some(r => r.prestation_id === prestationId && r.pricing_plan_id === selectedPlan.id);

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Grilles tarifaire - {viewMode === 'vehicles' ? 'Véhicules' : 'Prestations'}
          </Typography>
          {selectedPlan && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => navigate('/pricing/plans')}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  TARIF {selectedPlan.name.toUpperCase()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPlan.code}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton 
              value="vehicles" 
              sx={{ 
                bgcolor: viewMode === 'vehicles' ? '#9C27B0' : 'white',
                color: viewMode === 'vehicles' ? 'white' : 'inherit',
                '&.Mui-selected': { bgcolor: '#9C27B0', color: 'white' },
                '&.Mui-selected:hover': { bgcolor: '#7B1FA2' },
              }}
            >
              Véhicules
            </ToggleButton>
            <ToggleButton 
              value="prestations"
              sx={{ 
                bgcolor: viewMode === 'prestations' ? '#4CAF50' : 'white',
                color: viewMode === 'prestations' ? 'white' : 'inherit',
                '&.Mui-selected': { bgcolor: '#4CAF50', color: 'white' },
                '&.Mui-selected:hover': { bgcolor: '#388E3C' },
              }}
            >
              Prestations
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* VEHICLES VIEW */}
      {viewMode === 'vehicles' && (
        <>
          {/* Search Section */}
          <Paper sx={{ p: 2, mb: 2, border: '2px solid #1976D2', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Type véhicule</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e.target.value as number | '');
                    setSelectedCategory('');
                  }}
                  label="Type véhicule"
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Catégorie véhicule</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as number | '')}
                  label="Catégorie véhicule"
                  disabled={!selectedType}
                >
                  {filteredCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" sx={{ height: 40 }}>
                Afficher les grilles tarifaire
              </Button>
            </Box>
          </Paper>

          {/* Standard Rate Display */}
          {selectedType && selectedCategory && selectedCategoryRate && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#E3F2FD' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Tarif Standard - {selectedCategoryRate.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Prix HT</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedCategoryRate.rate_ht ? `${Number(selectedCategoryRate.rate_ht).toFixed(2)} €` : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Prix TTC</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedCategoryRate.rate_ttc ? `${Number(selectedCategoryRate.rate_ttc).toFixed(2)} €` : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">TVA</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedCategoryRate.tax_rate ? `${Number(selectedCategoryRate.tax_rate).toFixed(0)}%` : '20%'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton 
                  size="small" 
                  onClick={handleOpenStandardRateDialog}
                  sx={{ bgcolor: '#FFC107', color: 'black', '&:hover': { bgcolor: '#FFB300' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          )}

          {/* Pack Selection Tabs - Only show when type AND category are selected */}
          {selectedPlan && selectedType && selectedCategory && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              {filteredPacks.length > 0 ? (
                <>
                  {filteredPacks.map((pack) => (
                    <Button
                      key={pack.id}
                      variant={selectedPack?.id === pack.id ? 'contained' : 'outlined'}
                      onClick={() => handlePackSelect(pack)}
                      sx={{
                        borderRadius: '4px 4px 0 0',
                        bgcolor: selectedPack?.id === pack.id ? '#1976D2' : 'white',
                        color: selectedPack?.id === pack.id ? 'white' : 'text.primary',
                        minWidth: 120,
                      }}
                    >
                      {pack.name}
                    </Button>
                  ))}
                </>
              ) : (
                <Alert severity="info" sx={{ py: 0.5, flex: 1, mr: 1 }}>
                  Aucun pack pour cette catégorie. Cliquez sur <strong>+</strong> pour en créer un.
                  {packs.some(p => p.pricing_plan_id === selectedPlan?.id && p.vehicle_category_id == null) && (
                    <> Des packs <em>sans catégorie</em> existent pour ce plan — ils partagent les mêmes tarifs pour toutes les catégories.
                    Lancez la migration <code>split_universal_packs_per_category</code> (<code>php artisan migrate</code>) pour les convertir automatiquement en packs par catégorie.</>
                  )}
                </Alert>
              )}
              <IconButton
                size="small"
                sx={{ bgcolor: '#4CAF50', color: 'white', ml: 1 }}
                onClick={openCreatePackDialog}
                title="Ajouter un pack"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Main Content */}
          {selectedPack && (
            <Paper sx={{ p: 2 }}>
              {/* Configuration + Prestations */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976D2', mb: 1 }}>
                    Configuration générale
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                        Montant franchise :
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={packConfig.franchise_amount}
                        onChange={(e) => setPackConfig({ ...packConfig, franchise_amount: parseFloat(e.target.value) || 0 })}
                        InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>
                        Tarif par défaut :
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={packConfig.default_rate}
                        onChange={(e) => setPackConfig({ ...packConfig, default_rate: parseFloat(e.target.value) || 0 })}
                        InputProps={{ endAdornment: <InputAdornment position="end">€/jour</InputAdornment> }}
                        sx={{ width: 160 }}
                      />
                    </Box>
                    {(packConfig.franchise_amount !== (selectedPack.franchise_amount || 0) || 
                      packConfig.default_rate !== (selectedPack.default_rate || 0)) && (
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={handleSavePackConfig}
                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                      >
                        Enregistrer
                      </Button>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976D2', mb: 1 }}>
                    Prestations incluses dans l'offre
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedPack.included_prestations?.map((prestation) => (
                      <Chip
                        key={prestation.id}
                        label={`${prestation.name} x`}
                        onDelete={() => handleRemovePrestation(prestation.id)}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        sx={{ bgcolor: '#E3F2FD', border: '1px solid #90CAF9' }}
                      />
                    ))}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setPrestationDialogOpen(true)}
                      sx={{ bgcolor: '#FF9800' }}
                    >
                      Ajouter une prestation
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Tarif de base location */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Tarif de base location
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ bgcolor: '#4CAF50', color: 'white' }}
                    onClick={() => handleOpenRateDialog()}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setTierModelDialogOpen(true)}
                    sx={{ ml: 'auto' }}
                  >
                    Modèle de tarification
                  </Button>
                </Box>

                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0', py: 0.5, px: 1 } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1976D2' }}>
                        {sortedTiers.map((tier) => (
                          <TableCell key={tier.id} align="center" sx={{ color: 'white', fontWeight: 600 }}>
                            {tier.name}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Début</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Fin</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Couleur</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedRates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={sortedTiers.length + 4} align="center">
                            Aucun tarif défini
                          </TableCell>
                        </TableRow>
                      ) : (
                        groupedRates.map((group, idx) => (
                          <TableRow key={idx} hover>
                            {sortedTiers.map((tier) => (
                              <TableCell key={tier.id} align="center">
                                {group.prices[tier.id] ? `${Number(group.prices[tier.id]).toFixed(2)} €` : '-'}
                              </TableCell>
                            ))}
                            <TableCell align="center">
                              {format(parseISO(group.startDate), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell align="center">
                              {format(parseISO(group.endDate), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ width: 24, height: 24, bgcolor: group.color, borderRadius: 0.5, mx: 'auto' }} />
                            </TableCell>
                            <TableCell align="center">
                              <Button size="small" variant="contained" sx={{ bgcolor: '#f44336', mr: 0.5, minWidth: 'auto', px: 1 }}
                                onClick={async () => {
                                  if (!window.confirm('Supprimer tous les tarifs de cette période ?') || !selectedPack) return;
                                  const ids = Object.values(group.ids).filter(Boolean) as number[];
                                  try {
                                    await Promise.all(ids.map((rid) => dispatch(deleteRate(rid)).unwrap()));
                                    await refetchSeasonalRates(selectedPack.id);
                                  } catch (e) {
                                    console.error(e);
                                  }
                                }}>
                                delete
                              </Button>
                              <Button size="small" variant="contained" sx={{ bgcolor: '#FFC107', minWidth: 'auto', px: 1, color: 'black' }}
                                onClick={() => handleEditRateGroup(group)}>
                                edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Calendar View */}
              <Box sx={{ mb: 3, overflowX: 'auto' }}>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 900, '& .MuiTableCell-root': { border: '1px solid #e0e0e0', p: 0.25, fontSize: '0.65rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 80 }}></TableCell>
                        {Array.from({ length: 31 }, (_, i) => (
                          <TableCell key={i} align="center" sx={{ fontWeight: 600, minWidth: 24 }}>
                            {i + 1}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calendarData.map((month, monthIdx) => (
                        <TableRow key={monthIdx}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f5f5f5' }}>
                            {month.monthName}
                          </TableCell>
                          {month.days.map((day, dayIdx) => (
                            <TableCell
                              key={dayIdx}
                              align="center"
                              sx={{
                                bgcolor: day?.color || (day ? 'white' : '#f5f5f5'),
                                color: day?.color ? 'white' : 'inherit',
                              }}
                            >
                              {day?.dayAbbrev || ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Chart */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Tarif de base location
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} domain={[0, 35]} />
                    <RechartsTooltip />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    {sortedTiers.map((tier, index) => {
                      const colors = ['#2196F3', '#f44336', '#4caf50', '#ff9800'];
                      return (
                        <Line key={tier.id} type="monotone" dataKey={tier.name} stroke={colors[index % colors.length]} strokeWidth={2} dot={{ r: 4 }} />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* PRESTATIONS VIEW */}
      {viewMode === 'prestations' && (
        <Paper sx={{ p: 2 }}>
          {/* Prestations Table (associé aux tarifs) – effective rate = category if set, else type for that category */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Prestations (associé aux tarifs)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setTierModelDialogOpen(true)}
                sx={{ ml: 'auto' }}
              >
                Modèle de tarification
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Tarifs par défaut pour ce plan. Les tarifs personnalisés par catégorie ou type remplacent ces valeurs.
            </Typography>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0', py: 0.5, px: 1 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1976D2' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Libellé</TableCell>
                    {sortedTiers.map((tier) => (
                      <TableCell key={tier.id} align="center" sx={{ color: 'white', fontWeight: 600 }}>
                        {tier.name}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prestations.map((prestation) => {
                    const hasDefaultRate = prestationHasDefaultRate(prestation.id);
                    return (
                      <TableRow key={prestation.id} hover>
                        <TableCell>{prestation.name}</TableCell>
                        {sortedTiers.map((tier) => {
                          const defaultRate = getDefaultRate(prestation.id, tier.id);
                          return (
                            <TableCell key={tier.id} align="center">
                              {defaultRate != null ? `${defaultRate.toFixed(2)} €` : '-'}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color={hasDefaultRate ? 'primary' : 'success'}
                            onClick={() => handleOpenDefaultRateDialog(prestation)}
                          >
                            {hasDefaultRate ? 'Modifier' : 'Configurer'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Custom Tarifs Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Tarifs personnalisés
              </Typography>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCustomTarifType('category');
                  setCustomTarifDialogOpen(true);
                }}
              >
                Ajouter par catégorie
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCustomTarifType('type');
                  setEditingTypeRate(null);
                  setCustomTarifFormData(prev => ({ ...prev, type_id: '', prices: {} }));
                  setCustomTarifDialogOpen(true);
                }}
              >
                Ajouter par type
              </Button>
            </Box>

            {/* Category Custom Tarifs - Only show if there are custom rates */}
            {(() => {
              const categoriesWithRates = categories.filter(cat => 
                prestationRates.some(r => r.vehicle_category_id === cat.id)
              );
              if (categoriesWithRates.length === 0) return null;
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4CAF50' }}>
                    Par catégorie véhicule
                  </Typography>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0', py: 0.5, px: 1 } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#4CAF50' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Catégorie</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Prestation</TableCell>
                          {sortedTiers.map((tier) => (
                            <TableCell key={tier.id} align="center" sx={{ color: 'white', fontWeight: 600 }}>
                              {tier.name}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoriesWithRates.map((category) => {
                          const categoryPrestations = prestations.filter(p => 
                            prestationRates.some(r => r.prestation_id === p.id && r.vehicle_category_id === category.id)
                          );
                          return categoryPrestations.map((prestation, index) => (
                            <TableRow key={`${category.id}-${prestation.id}`} hover>
                              {index === 0 && (
                                <TableCell 
                                  rowSpan={categoryPrestations.length} 
                                  sx={{ fontWeight: 600, bgcolor: '#E8F5E9', verticalAlign: 'top' }}
                                >
                                  {category.name}
                                </TableCell>
                              )}
                              <TableCell>{prestation.name}</TableCell>
                              {sortedTiers.map((tier) => {
                                const rate = prestationRates.find(
                                  r => r.prestation_id === prestation.id && 
                                       r.vehicle_category_id === category.id && 
                                       r.pricing_tier_id === tier.id
                                );
                                return (
                                  <TableCell key={tier.id} align="center">
                                    {rate ? `${Number(rate.rate_ht).toFixed(2)} €` : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  onClick={() => handleOpenPrestationRateDialog(prestation, category.id)}
                                >
                                  Modifier
                                </Button>
                              </TableCell>
                            </TableRow>
                          ));
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}

            {/* Type Custom Tarifs - Only show if there are custom type rates (independent of category) */}
            {(() => {
              const typesWithRates = types.filter(type =>
                typeRates.some(r => r.vehicle_type_id === type.id)
              );
              if (typesWithRates.length === 0) return null;
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#FF9800' }}>
                    Par type véhicule
                  </Typography>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0', py: 0.5, px: 1 } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#FF9800' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Prestation</TableCell>
                          {sortedTiers.map((tier) => (
                            <TableCell key={tier.id} align="center" sx={{ color: 'white', fontWeight: 600 }}>
                              {tier.name}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {typesWithRates.map((type) => {
                          const typePrestations = prestations.filter(p =>
                            typeRates.some(r => r.prestation_id === p.id && r.vehicle_type_id === type.id)
                          );
                          return typePrestations.map((prestation, index) => (
                            <TableRow key={`${type.id}-${prestation.id}`} hover>
                              {index === 0 && (
                                <TableCell
                                  rowSpan={typePrestations.length}
                                  sx={{ fontWeight: 600, bgcolor: '#FFF3E0', verticalAlign: 'top' }}
                                >
                                  {type.name}
                                </TableCell>
                              )}
                              <TableCell>{prestation.name}</TableCell>
                              {sortedTiers.map((tier) => {
                                const rate = typeRates.find(
                                  r => r.prestation_id === prestation.id &&
                                       r.vehicle_type_id === type.id &&
                                       r.pricing_tier_id === tier.id
                                );
                                return (
                                  <TableCell key={tier.id} align="center">
                                    {rate ? `${Number(rate.rate_ht).toFixed(2)} €` : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => handleOpenTypeRateDialog(prestation, type.id)}
                                >
                                  Modifier
                                </Button>
                              </TableCell>
                            </TableRow>
                          ));
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}

            {/* Empty state message */}
            {(() => {
              const hasAnyRates = prestationRates.length > 0 || typeRates.length > 0;
              if (hasAnyRates) return null;
              return (
                <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography color="text.secondary">
                    Aucun tarif personnalisé configuré. Utilisez les boutons ci-dessus pour ajouter des tarifs par catégorie ou par type.
                  </Typography>
                </Box>
              );
            })()}
          </Box>

        </Paper>
      )}

      {/* Rate Form Dialog */}
      <Dialog open={rateDialogOpen} onClose={handleCloseRateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Ajouter/Modifier tarif de base location
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                Début
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={rateFormData.start_date}
                onChange={(e) => setRateFormData({ ...rateFormData, start_date: e.target.value })}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                Fin
              </Typography>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={rateFormData.end_date}
                onChange={(e) => setRateFormData({ ...rateFormData, end_date: e.target.value })}
              />
            </Box>
            <Box
              sx={{ width: 40, height: 40, bgcolor: rateFormData.color, borderRadius: 1, cursor: 'pointer', border: '1px solid #ccc' }}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'color';
                input.value = rateFormData.color;
                input.addEventListener('change', (e) => {
                  setRateFormData({ ...rateFormData, color: (e.target as HTMLInputElement).value });
                });
                input.click();
              }}
            />
          </Box>
          
          <Grid container spacing={2}>
            {sortedTiers.map((tier) => (
              <Grid size={{ xs: 6 }} key={tier.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>{tier.name} :</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={rateFormData.prices[tier.id] || ''}
                    onChange={(e) => {
                      setRateFormData({
                        ...rateFormData,
                        prices: { ...rateFormData.prices, [tier.id]: parseFloat(e.target.value) || 0 },
                      });
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <IconButton size="small" onClick={handleCloseRateDialog}><ArrowBackIcon /></IconButton>
          <Button variant="contained" onClick={handleSaveRate} disabled={!rateFormData.start_date || !rateFormData.end_date}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prestation Selection Dialog */}
      <Dialog open={prestationDialogOpen} onClose={() => setPrestationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ajouter une prestation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {prestations
              .filter(p => !selectedPack?.included_prestations?.some(ip => ip.id === p.id))
              .map((prestation) => (
                <Chip
                  key={prestation.id}
                  label={prestation.name}
                  onClick={() => {
                    handleAddPrestation(prestation);
                    setPrestationDialogOpen(false);
                  }}
                  sx={{ cursor: 'pointer' }}
                  variant="outlined"
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrestationDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Prestation Rate Dialog */}
      <Dialog open={prestationRateDialogOpen} onClose={() => { setPrestationRateDialogOpen(false); setSelectedCategoryForRate(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Modifier tarif prestation
          {selectedCategoryForRate && (
            <Typography variant="body2" color="text.secondary">
              Catégorie: {categories.find(c => c.id === selectedCategoryForRate)?.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel>Prestation</InputLabel>
            <Select
              value={prestationRateFormData.prestation_id}
              onChange={(e) => setPrestationRateFormData({ ...prestationRateFormData, prestation_id: e.target.value })}
              label="Prestation"
              disabled={!!selectedPrestationForRate}
            >
              {prestations.map((p) => (
                <MenuItem key={p.id} value={p.id.toString()}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Grid container spacing={2}>
            {sortedTiers.map((tier) => (
              <Grid size={{ xs: 6 }} key={tier.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>{tier.name} :</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={prestationRateFormData.prices[tier.id] || ''}
                    onChange={(e) => {
                      setPrestationRateFormData({
                        ...prestationRateFormData,
                        prices: { ...prestationRateFormData.prices, [tier.id]: parseFloat(e.target.value) || 0 },
                      });
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <IconButton size="small" onClick={() => { setPrestationRateDialogOpen(false); setSelectedCategoryForRate(null); }}><ArrowBackIcon /></IconButton>
          <Button variant="contained" onClick={handleSavePrestationRate} disabled={!prestationRateFormData.prestation_id}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Default Rate Dialog (associé aux tarifs - first table) */}
      <Dialog open={defaultRateDialogOpen} onClose={() => { setDefaultRateDialogOpen(false); setEditingDefaultRate(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976D2', color: 'white' }}>
          {editingDefaultRate ? 'Modifier' : 'Configurer'} tarifs par défaut (associé aux tarifs)
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prestation: <strong>{editingDefaultRate?.name}</strong>
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Tarifs par durée
          </Typography>
          <Grid container spacing={2}>
            {sortedTiers.map((tier) => (
              <Grid size={{ xs: 6 }} key={tier.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>{tier.name} :</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={defaultRateFormData.prices[tier.id] || ''}
                    onChange={(e) => {
                      setDefaultRateFormData({
                        ...defaultRateFormData,
                        prices: { ...defaultRateFormData.prices, [tier.id]: parseFloat(e.target.value) || 0 },
                      });
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Ces tarifs par défaut s&apos;appliquent sauf si un tarif personnalisé par catégorie ou type est défini.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={() => { setDefaultRateDialogOpen(false); setEditingDefaultRate(null); }}>Annuler</Button>
          <Button variant="contained" color="primary" onClick={handleSaveDefaultRate} disabled={!defaultRateFormData.prestation_id}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Tarif Dialog */}
      <Dialog open={customTarifDialogOpen} onClose={() => { setCustomTarifDialogOpen(false); setEditingTypeRate(null); setCustomTarifType('category'); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          bgcolor: customTarifType === null ? '#1976D2' : customTarifType === 'category' ? '#4CAF50' : '#FF9800',
          color: 'white',
        }}>
          {customTarifType === null
            ? 'Configurer les tarifs (associé aux tarifs)'
            : `${editingTypeRate ? 'Modifier' : 'Ajouter'} tarif personnalisé par ${customTarifType === 'category' ? 'catégorie' : 'type'}`}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Prestation</InputLabel>
            <Select
              value={customTarifFormData.prestation_id}
              onChange={(e) => setCustomTarifFormData({ ...customTarifFormData, prestation_id: e.target.value })}
              label="Prestation"
            >
              {prestations.map((p) => (
                <MenuItem key={p.id} value={p.id.toString()}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {customTarifType === null ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Associer à</Typography>
              <ToggleButtonGroup
                value={customTarifType ?? ''}
                exclusive
                onChange={(_, value) => value != null && setCustomTarifType(value as 'category' | 'type')}
                size="small"
                fullWidth
              >
                <ToggleButton value="category" sx={{ flex: 1 }}>Par catégorie véhicule</ToggleButton>
                <ToggleButton value="type" sx={{ flex: 1 }}>Par type véhicule</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ) : customTarifType === 'category' ? (
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Catégorie véhicule</InputLabel>
              <Select
                value={customTarifFormData.category_id}
                onChange={(e) => setCustomTarifFormData({ ...customTarifFormData, category_id: e.target.value })}
                label="Catégorie véhicule"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id.toString()}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Type véhicule</InputLabel>
              <Select
                value={customTarifFormData.type_id}
                onChange={(e) => setCustomTarifFormData({ ...customTarifFormData, type_id: e.target.value })}
                label="Type véhicule"
              >
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id.toString()}>{type.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {customTarifType !== null && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Tarifs par durée
              </Typography>
              <Grid container spacing={2}>
                {sortedTiers.map((tier) => (
                  <Grid size={{ xs: 6 }} key={tier.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 80 }}>{tier.name} :</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={customTarifFormData.prices[tier.id] || ''}
                        onChange={(e) => {
                          setCustomTarifFormData({
                            ...customTarifFormData,
                            prices: { ...customTarifFormData.prices, [tier.id]: parseFloat(e.target.value) || 0 },
                          });
                        }}
                        InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {customTarifType === 'type' && customTarifFormData.type_id && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Tarif propre au type (indépendant des catégories).
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={() => { setCustomTarifDialogOpen(false); setCustomTarifType('category'); }}>Annuler</Button>
          <Button 
            variant="contained" 
            color={customTarifType === 'category' ? 'success' : customTarifType === 'type' ? 'warning' : 'primary'}
            onClick={handleSaveCustomTarif} 
            disabled={!customTarifFormData.prestation_id || customTarifType === null || (customTarifType === 'category' ? !customTarifFormData.category_id : !customTarifFormData.type_id)}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modèle de tarification Dialog */}
      <Dialog open={tierModelDialogOpen} onClose={() => setTierModelDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Modèle de tarification
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0', py: 0.5 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976D2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Libellé</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Min jours</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Max jours</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Ordre</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTiers.map((tier) => (
                  <TableRow key={tier.id}>
                    <TableCell>{tier.name}</TableCell>
                    <TableCell align="center">{tier.min_days}</TableCell>
                    <TableCell align="center">{tier.max_days}</TableCell>
                    <TableCell align="center">{tier.sort_order}</TableCell>
                    <TableCell align="center">
                      <Button 
                        size="small" 
                        variant="contained" 
                        sx={{ bgcolor: '#FFC107', mr: 0.5, minWidth: 'auto', px: 1, color: 'black' }}
                        onClick={() => handleOpenTierForm(tier)}
                      >
                        edit
                      </Button>
                      <Button 
                        size="small" 
                        variant="contained" 
                        sx={{ bgcolor: '#f44336', minWidth: 'auto', px: 1 }}
                        onClick={() => handleDeleteTier(tier.id)}
                      >
                        delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={() => setTierModelDialogOpen(false)}>Fermer</Button>
          <Button variant="contained" color="success" onClick={() => handleOpenTierForm()}>
            + Ajouter un niveau
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tier Form Dialog */}
      <Dialog open={tierFormDialogOpen} onClose={handleCloseTierForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          {editingTier ? 'Modifier le niveau tarifaire' : 'Nouveau niveau tarifaire'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Libellé"
              fullWidth
              size="small"
              value={tierFormData.name}
              onChange={(e) => setTierFormData({ ...tierFormData, name: e.target.value })}
              placeholder="Ex: 1-5 jours"
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Minimum de jours"
                  type="number"
                  fullWidth
                  size="small"
                  value={tierFormData.min_days}
                  onChange={(e) => setTierFormData({ ...tierFormData, min_days: parseInt(e.target.value) || 1 })}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Maximum de jours"
                  type="number"
                  fullWidth
                  size="small"
                  value={tierFormData.max_days}
                  onChange={(e) => setTierFormData({ ...tierFormData, max_days: parseInt(e.target.value) || 1 })}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Ordre d'affichage"
              type="number"
              fullWidth
              size="small"
              value={tierFormData.sort_order}
              onChange={(e) => setTierFormData({ ...tierFormData, sort_order: parseInt(e.target.value) || 0 })}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={handleCloseTierForm}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveTier}
            disabled={!tierFormData.name || tierFormData.min_days <= 0 || tierFormData.max_days <= 0}
          >
            {editingTier ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Standard Rate Dialog */}
      <Dialog open={standardRateDialogOpen} onClose={() => { setStandardRateDialogOpen(false); setStandardRateError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Modifier le tarif standard - {selectedCategoryRate?.name}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {standardRateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStandardRateError(null)}>
              {standardRateError}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Prix HT"
              type="number"
              fullWidth
              size="small"
              value={standardRateFormData.rate_ht}
              onChange={(e) => setStandardRateFormData({ ...standardRateFormData, rate_ht: parseFloat(e.target.value) || 0 })}
              InputProps={{ 
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
            <TextField
              label="Taux TVA"
              type="number"
              fullWidth
              size="small"
              value={standardRateFormData.tax_rate}
              onChange={(e) => setStandardRateFormData({ ...standardRateFormData, tax_rate: parseFloat(e.target.value) || 0 })}
              InputProps={{ 
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100 }
              }}
            />
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Prix TTC calculé: <strong>{(standardRateFormData.rate_ht * (1 + standardRateFormData.tax_rate / 100)).toFixed(2)} €</strong>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={() => setStandardRateDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveStandardRate}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Pack Dialog (from grid - same page) */}
      <Dialog open={createPackDialogOpen} onClose={() => setCreatePackDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          Nouveau Pack
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">Plan : {selectedPlan?.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nom du pack"
                value={createPackFormData.name}
                onChange={(e) => setCreatePackFormData({ ...createPackFormData, name: e.target.value })}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                value={createPackFormData.description}
                onChange={(e) => setCreatePackFormData({ ...createPackFormData, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie véhicule</InputLabel>
                <Select
                  value={createPackFormData.vehicle_category_id}
                  onChange={(e) => setCreatePackFormData({ ...createPackFormData, vehicle_category_id: e.target.value })}
                  label="Catégorie véhicule"
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {filteredCategories.map((cat: VehicleCategory) => (
                    <MenuItem key={cat.id} value={cat.id.toString()}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Montant franchise"
                type="number"
                value={createPackFormData.franchise_amount}
                onChange={(e) => setCreatePackFormData({ ...createPackFormData, franchise_amount: parseFloat(e.target.value) || 0 })}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Tarif par défaut"
                type="number"
                value={createPackFormData.default_rate}
                onChange={(e) => setCreatePackFormData({ ...createPackFormData, default_rate: parseFloat(e.target.value) || 0 })}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">€/jour</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Couleur :</Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: createPackFormData.color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                  }}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'color';
                    input.value = createPackFormData.color;
                    input.addEventListener('change', (e) => {
                      setCreatePackFormData({ ...createPackFormData, color: (e.target as HTMLInputElement).value });
                    });
                    input.click();
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={() => setCreatePackDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreatePackSubmit}
            disabled={!createPackFormData.name}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingGrid;
